import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  Input,
  Modal,
  ModalFooter,
} from "@insights/ui";
import { ArrowUpRight, CircleX } from "lucide-react";
import { useParams } from "react-router";
import { PageHeader } from "../../../shared/components";
import {
  formatMonthlyPrice,
  getCurrencyLabel,
} from "../../../shared/helpers/format-monthly-price";
import { AppShell } from "../../../shared/layout";
import { BillingProfileForm } from "../components/BillingProfileForm";
import {
  getCancellationPlan,
  getUpgradePlans,
  type UpgradePlan,
} from "../helpers/get-upgrade-plans";
import { getBillingPlanControls } from "../helpers/get-billing-plan-controls";
import {
  formatPerSeatPricingNote,
  formatPlanStorage,
  formatPlanUsers,
  getPlanDisplayName,
  getPlanMonthlyCost,
  getPlanStorageMb,
} from "../helpers/plan-display";
import { useBilling } from "../hooks/useBilling";

export function BillingPage() {
  const { workspaceId } = useParams();
  const billing = useBilling(workspaceId);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [selectedUpgrade, setSelectedUpgrade] =
    useState<UpgradePlan | null>(null);
  const [upgradeSeats, setUpgradeSeats] = useState<number | "">(1);
  const [upgradeError, setUpgradeError] = useState("");
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertError, setRevertError] = useState("");
  const [revertingChange, setRevertingChange] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [reactivationError, setReactivationError] = useState("");
  const [reactivatingSubscription, setReactivatingSubscription] =
    useState(false);
  const upgradePlans = getUpgradePlans(billing.products.data?.items ?? []);
  const cancellationPlan = getCancellationPlan(
    billing.products.data?.items ?? [],
  );
  const subscription = billing.query.data?.usage?.subscription;
  const currentProduct = billing.products.data?.items.find(
    (product) => product.id === subscription?.product_id,
  );
  const currentPrice = currentProduct?.prices.find(
    (price) => price.id === subscription?.price_id,
  );
  const perSeatPricingNote = currentPrice
    ? formatPerSeatPricingNote(
        currentPrice,
        subscription?.currency ?? currentProduct?.currency ?? "EUR",
      )
    : null;
  const hasSubscriptionMismatch = Boolean(
    billing.query.data?.subscription_mismatch,
  );
  const {
    canChangePlan,
    canCancelSubscription,
    canResumeSubscription,
    changePlanLabel,
    isStartingPaidSubscription,
  } = getBillingPlanControls({
    role: billing.query.data?.role,
    subscriptionCost: subscription?.cost_per_month,
    hasSubscriptionMismatch,
    hasStripeSubscription: Boolean(
      billing.query.data?.stripe_subscription_id,
    ),
    cancellationScheduled: Boolean(
      billing.query.data?.stripe_cancel_at_period_end,
    ),
    hasCancellationPlan: Boolean(cancellationPlan),
  });
  const minimumUpgradeSeats =
    selectedUpgrade?.price.baseSeats ?? 1;
  const maximumUpgradeSeats = selectedUpgrade?.price.maxSeats;
  const validUpgradeSeats =
    selectedUpgrade?.price.pricingModel !== "per_seat" ||
    (typeof upgradeSeats === "number" &&
      Number.isInteger(upgradeSeats) &&
      upgradeSeats >= minimumUpgradeSeats &&
      (maximumUpgradeSeats === null ||
        maximumUpgradeSeats === undefined ||
        upgradeSeats <= maximumUpgradeSeats));
  const displayedUpgradeSeats =
    typeof upgradeSeats === "number" ? upgradeSeats : minimumUpgradeSeats;
  const canEditBilling = billing.query.data?.role === "owner";

  function openUpgrade() {
    const currentPlan = upgradePlans.find(
      (plan) =>
        plan.product.id === subscription?.product_id &&
        plan.price.id === subscription.price_id,
    );
    const initialPlan = currentPlan ?? selectedUpgrade ?? upgradePlans[0] ?? null;
    const initialSeats =
      initialPlan?.price.pricingModel === "per_seat" && currentPlan
        ? subscription?.seats ?? 1
        : initialPlan?.price.baseSeats ?? 1;
    setSelectedUpgrade(initialPlan);
    setUpgradeSeats(Math.max(initialSeats, 1));
    setUpgradeError("");
    setUpgradeOpen(true);
  }

  async function startUpgrade() {
    if (!selectedUpgrade) {
      setUpgradeError("Select a paid plan to continue.");
      return;
    }
    if (!validUpgradeSeats || typeof upgradeSeats !== "number") {
      setUpgradeError("Enter a valid number of active seats.");
      return;
    }

    setUpgradeError("");
    try {
      const result = await billing.upgrade.mutateAsync({
        productId: selectedUpgrade.product.id,
        priceId: selectedUpgrade.price.id,
        seats: upgradeSeats,
      });
      if (!result.url) {
        setUpgradeOpen(false);
      }
    } catch (caught) {
      setUpgradeError(
        caught instanceof Error ? caught.message : "Unable to start checkout",
      );
    }
  }

  async function revertScheduledPlanChange() {
    setRevertError("");
    setRevertingChange(true);
    try {
      await billing.action("revert-plan-change");
      setRevertOpen(false);
    } catch (caught) {
      setRevertError(
        caught instanceof Error
          ? caught.message
          : "Unable to cancel the scheduled plan change",
      );
    } finally {
      setRevertingChange(false);
    }
  }

  async function cancelSubscription() {
    setCancelError("");
    setCancelingSubscription(true);
    try {
      await billing.action("cancel");
      setCancelOpen(false);
    } catch (caught) {
      setCancelError(
        caught instanceof Error
          ? caught.message
          : "Unable to cancel the subscription",
      );
    } finally {
      setCancelingSubscription(false);
    }
  }

  async function reactivateSubscription() {
    setReactivationError("");
    setReactivatingSubscription(true);
    try {
      await billing.action("reactivate");
    } catch (caught) {
      setReactivationError(
        caught instanceof Error
          ? caught.message
          : "Unable to resume subscription renewal",
      );
    } finally {
      setReactivatingSubscription(false);
    }
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Plan & subscription" title="Billing" />
      <div className="admin-grid billing-grid">
        <Card className="metric billing-subscription-card">
          <div className="billing-plan-title-row">
            <strong>
              {subscription
                ? getPlanDisplayName(subscription.price_name)
                : "No active plan"}
            </strong>
          </div>
          {subscription && (
            <div className="plan-inclusions">
              <span>
                {formatPlanStorage(
                  subscription.max_storage_bytes / 1024 ** 2,
                )}
              </span>
              <span>
                {formatPlanUsers(
                  subscription.pricing_model,
                  subscription.seats,
                )}
              </span>
            </div>
          )}
          {subscription && currentProduct?.description && (
            <p className="billing-plan-description">
              {currentProduct.description}
            </p>
          )}
          <p className="billing-plan-price">
            {subscription ? (
              <>
                {subscription.cost_per_month}{" "}
                {getCurrencyLabel(subscription.currency)}{" "}
                gross/month
                {subscription.cost_per_month > 0 &&
                  !billing.query.data?.stripe_cancel_at_period_end && (
                    <span className="billing-plan-next-charge">
                      {subscription.current_period_end
                        ? `• Next charge on ${new Date(
                            subscription.current_period_end,
                          ).toLocaleDateString()}`
                        : "• Renews monthly"}
                    </span>
                  )}
              </>
            ) : (
              "Terrabuild access requires an active plan."
            )}
          </p>
          {perSeatPricingNote && (
            <p className="billing-plan-pricing-note">{perSeatPricingNote}</p>
          )}
          {(canChangePlan || canCancelSubscription) && (
            <div className="billing-plan-actions">
              {canChangePlan && (
                <Button icon={<ArrowUpRight />} onClick={openUpgrade}>
                  {changePlanLabel}
                </Button>
              )}
              {canCancelSubscription && (
                <Button
                  color="danger"
                  onClick={() => {
                    setCancelError("");
                    setCancelOpen(true);
                  }}
                >
                  <CircleX />
                  Cancel subscription
                </Button>
              )}
            </div>
          )}
          {subscription?.scheduled_price_name &&
            subscription.scheduled_effective_at && (
              <p>
                Change to {getPlanDisplayName(subscription.scheduled_price_name)}
                {subscription.scheduled_seats
                  ? ` (${subscription.scheduled_seats} seat${
                      subscription.scheduled_seats === 1 ? "" : "s"
                    })`
                  : ""} scheduled for {new Date(
                  subscription.scheduled_effective_at,
                ).toLocaleDateString()}.
              </p>
            )}
          {subscription?.stripe_schedule_id && (
            <Button
              color="neutral"
              onClick={() => {
                setRevertError("");
                setRevertOpen(true);
              }}
            >
              Cancel scheduled change
            </Button>
          )}
          {billing.query.data?.stripe_cancel_at_period_end && (
            <>
              <div className="billing-cancellation-notice">
                <span>
                  Your paid plan remains active until {billing.query.data
                    .stripe_cancellation_at
                    ? new Date(
                        billing.query.data.stripe_cancellation_at,
                      ).toLocaleDateString()
                    : "the end of the current billing period"}
                  . It will then switch to Free.
                </span>
                {canResumeSubscription && (
                  <Button
                    color="danger"
                    size="small"
                    onClick={() => void reactivateSubscription()}
                    disabled={reactivatingSubscription}
                  >
                    {reactivatingSubscription
                      ? "Resuming…"
                      : "Resume renewal"}
                  </Button>
                )}
              </div>
              {reactivationError && (
                <p className="error">{reactivationError}</p>
              )}
            </>
          )}
        </Card>
        {billing.query.data?.billing_profile && (
          <BillingProfileForm
            canEdit={canEditBilling}
            profile={billing.query.data.billing_profile}
            saving={billing.updateProfile.isPending}
            onUpdate={(profile) => billing.updateProfile.mutateAsync(profile)}
          />
        )}
        <Card
          as="section"
          className="metric billing-actors-card"
          aria-labelledby="billing-actors-title"
        >
          <h2 className="section-card-title" id="billing-actors-title">
            Build actors
          </h2>
          <div className="billing-actors-list">
            {billing.query.data?.actors?.map((actor) => (
              <div
                className="row"
                key={`${actor.period_start}-${actor.actor_id}`}
              >
                <div>
                  <strong>{actor.actor_name ?? actor.actor_id}</strong>
                  <span>{actor.actor_id}</span>
                </div>
                <Badge
                  className={
                    actor.status === "accepted" ? "success" : "failed"
                  }
                >
                  {actor.status}
                </Badge>
              </div>
            ))}
            {!billing.query.data?.actors?.length && (
              <p className="muted">No actors observed yet.</p>
            )}
          </div>
        </Card>
      </div>
      <Modal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        title={
          !subscription
            ? "Choose a plan"
            : isStartingPaidSubscription
              ? "Upgrade plan"
              : "Change plan"
        }
        description={
          isStartingPaidSubscription
            ? "Choose a paid plan. Stripe will confirm payment before the workspace entitlement changes."
            : "Upgrades are prorated immediately. Lower-cost plans take effect at the next billing period."
        }
        className="billing-upgrade-modal"
      >
        {billing.products.isLoading && <p>Loading available plans…</p>}
        {billing.products.error && (
          <p className="error">{billing.products.error.message}</p>
        )}
        {!billing.products.isLoading && upgradePlans.length === 0 && (
          <p className="muted">No paid plans are currently available.</p>
        )}
        <div className="plan-list billing-upgrade-plans">
          {upgradePlans.map((plan) => {
            const selected =
              selectedUpgrade?.product.id === plan.product.id &&
              selectedUpgrade.price.id === plan.price.id;
            const planSeats = selected
              ? displayedUpgradeSeats
              : plan.price.baseSeats ?? 1;
            const monthly = formatMonthlyPrice(
              plan.price,
              plan.product.currency,
            );
            const monthlyTotal =
              plan.price.pricingModel === "per_seat"
                ? `${getPlanMonthlyCost(plan.price, planSeats)} ${
                    getCurrencyLabel(plan.product.currency)
                  }/month total`
                : null;

            return (
              <button
                type="button"
                className={`plan-option${selected ? " selected" : ""}`}
                key={`${plan.product.id}-${plan.price.id}`}
                onClick={() => {
                  setSelectedUpgrade(plan);
                  const selectingCurrentPlan =
                    plan.product.id === subscription?.product_id &&
                    plan.price.id === subscription.price_id;
                  setUpgradeSeats(
                    selectingCurrentPlan
                      ? subscription.seats
                      : Math.max(
                          plan.price.baseSeats ?? 1,
                          1,
                        ),
                  );
                }}
              >
                <strong>{getPlanDisplayName(plan.price.name)}</strong>
                <span>{plan.product.description}</span>
                <b>{monthly}</b>
                <small>
                  {formatPlanStorage(
                    getPlanStorageMb(
                      plan.price,
                      planSeats,
                    ),
                  )}
                </small>
                <small>
                  {formatPlanUsers(
                    plan.price.pricingModel,
                    planSeats,
                  )}
                </small>
                {monthlyTotal && <small>{monthlyTotal}</small>}
                {plan.product.id === subscription?.product_id &&
                  plan.price.id === subscription.price_id && (
                    <small>Current plan</small>
                  )}
              </button>
            );
          })}
        </div>
        {selectedUpgrade?.price.pricingModel === "per_seat" && (
          <label className="seat-selector billing-upgrade-seats">
            Active seats
            <Input
              type="number"
              min={
                selectedUpgrade.price.baseSeats ?? 1
              }
              max={selectedUpgrade.price.maxSeats ?? undefined}
              value={upgradeSeats}
              placeholder="1"
              required
              onChange={(event) => {
                setUpgradeSeats(
                  event.target.value === "" ? "" : Number(event.target.value),
                );
              }}
            />
          </label>
        )}
        {upgradeError && <p className="error">{upgradeError}</p>}
        <ModalFooter>
          <Button
            color="neutral"
            onClick={() => setUpgradeOpen(false)}
            disabled={billing.upgrade.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void startUpgrade()}
            disabled={
              !selectedUpgrade ||
              !validUpgradeSeats ||
              billing.upgrade.isPending
            }
          >
            {billing.upgrade.isPending
              ? isStartingPaidSubscription
                ? "Opening Stripe…"
                : "Updating plan…"
              : isStartingPaidSubscription
                ? "Continue to Stripe"
                : "Change plan"}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel at the end of the billing period?"
        description={
          subscription?.current_period_end
            ? `Your paid plan remains active until ${new Date(
                subscription.current_period_end,
              ).toLocaleDateString()}. Stripe will stop renewal at that time.`
            : "Your paid plan remains active through the current billing period. Stripe will stop renewal at the end of that period."
        }
      >
        {cancellationPlan && (
          <div className="billing-cancellation-summary">
            <strong>
              {getPlanDisplayName(cancellationPlan.price.name)}
            </strong>
            <span>
              {formatPlanStorage(cancellationPlan.price.baseStorageMb)}
            </span>
            <span>
              {formatPlanUsers(
                cancellationPlan.price.pricingModel,
                cancellationPlan.price.baseSeats ?? 1,
              )}
            </span>
          </div>
        )}
        <p className="billing-cancellation-consequence">
          At the end of the billing period, the workspace switches to Free. If
          it exceeds the Free storage limit then, new builds and uploads will be
          blocked until usage is reduced.
        </p>
        {cancelError && <p className="error">{cancelError}</p>}
        <ModalFooter>
          <Button
            color="neutral"
            onClick={() => setCancelOpen(false)}
            disabled={cancelingSubscription}
          >
            Keep subscription
          </Button>
          <Button
            color="danger"
            onClick={() => void cancelSubscription()}
            disabled={cancelingSubscription}
          >
            {cancelingSubscription
              ? "Scheduling…"
              : "Cancel at period end"}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        open={revertOpen}
        onOpenChange={setRevertOpen}
        title="Cancel scheduled plan change?"
        description={
          subscription?.scheduled_price_name &&
          subscription.scheduled_effective_at
            ? `This keeps ${getPlanDisplayName(subscription.price_name)} active and removes the scheduled change to ${getPlanDisplayName(subscription.scheduled_price_name)} on ${new Date(
                subscription.scheduled_effective_at,
              ).toLocaleDateString()}.`
            : "This keeps the current plan active and removes the scheduled plan change."
        }
      >
        <p>The workspace will remain on its current plan without interruption.</p>
        {revertError && <p className="error">{revertError}</p>}
        <ModalFooter>
          <Button
            color="neutral"
            onClick={() => setRevertOpen(false)}
            disabled={revertingChange}
          >
            Keep scheduled change
          </Button>
          <Button
            onClick={() => void revertScheduledPlanChange()}
            disabled={revertingChange}
          >
            {revertingChange ? "Canceling…" : "Cancel scheduled change"}
          </Button>
        </ModalFooter>
      </Modal>
    </AppShell>
  );
}
